/**
 * M7 S25: the roll call (spec §8.1, C6): a short comms sequence before every commit that shows who stands with the
 * player. Reyes (ally, cool, hostile or absent), Okafor (aboard, held, safe or absent), Kell's trust (high, wary or
 * low, from `finale.json rollCall.kellTrust`), Duarte only if Hale doesn't know about her (and she isn't exposed),
 * and every assisted claim (`claim_assisted_<n>`) by name. Before Blackmail → Guardians an allied Reyes is already
 * `cool` ("you cashed her cheques first", §8.3): the roll call says so before the commit lands it.
 *
 * Each entry is one `comms/finale.json` node; every node ends on a plain reply, so the UI (S39) plays them in order.
 * Pure; reads the Career only.
 */
import type { Career } from '../../career/types'
import type { FinaleConfig } from '../schemas'
import { hasFlag, storyOf } from '../state'
import { STORY_FLAGS, type FinalePath, type Speaker, type StoryFlag } from '../types'
import { pathsInForce } from '../clock'
import { finaleData } from './paths'

export const ROLL_CALL_WHO = ['reyes', 'okafor', 'kell', 'duarte', 'claim'] as const
export type RollCallWho = (typeof ROLL_CALL_WHO)[number]

export interface RollCallEntry {
  who: RollCallWho
  /** The state shown (`ally`, `aboard`, `high`, `present`, the claim tier). */
  state: string
  speaker: Speaker
  nodeId: string
}

export type KellTrust = 'high' | 'wary' | 'low'

/** Kell's trust from the story so far: Σ flag weights + Holdfast contracts run. */
export function kellTrust(career: Career, cfg: FinaleConfig = finaleData().finale): KellTrust {
  const t = cfg.rollCall.kellTrust
  const story = storyOf(career)
  let score = (story.holdfastContracts ?? 0) * t.perHoldfastContract
  for (const flag of STORY_FLAGS) {
    const w = t.weights[flag]
    if (w !== undefined && story.flags[flag] === true) score += w
  }
  if (score >= t.highAt) return 'high'
  if (score <= t.lowAt) return 'low'
  return 'wary'
}

const ASSISTED: readonly { tier: number; flag: StoryFlag }[] = [
  { tier: 2, flag: 'claim_assisted_2' },
  { tier: 3, flag: 'claim_assisted_3' },
  { tier: 4, flag: 'claim_assisted_4' },
  { tier: 5, flag: 'claim_assisted_5' },
]

/** The roll call before committing `to` (the first commit or a ladder move). */
export function rollCall(career: Career, to: FinalePath, cfg: FinaleConfig = finaleData().finale): RollCallEntry[] {
  const story = storyOf(career)
  const out: RollCallEntry[] = []

  // Reyes: absent when never met or dead (Kell says so).
  const leavingBlackmail = pathsInForce(story.finale).includes('blackmail') && to !== 'blackmail'
  const reyes = story.reyesState
  if (reyes === undefined || reyes === 'dead') {
    out.push({ who: 'reyes', state: 'absent', speaker: 'kell', nodeId: 'finale.roll.reyes.absent' })
  } else if (reyes === 'ally' && leavingBlackmail && to === 'guardians') {
    out.push({ who: 'reyes', state: 'cool', speaker: 'reyes', nodeId: 'finale.roll.reyes.cheques' })
  } else {
    out.push({ who: 'reyes', state: reyes, speaker: reyes === 'hostile' ? 'kell' : 'reyes', nodeId: `finale.roll.reyes.${reyes}` })
  }

  // Okafor: aboard, held or safe; otherwise absent (never met, still at the hideout, or lost).
  const okafor = story.okaforState
  if (okafor === 'aboard' || okafor === 'safe') {
    out.push({ who: 'okafor', state: okafor, speaker: 'okafor', nodeId: `finale.roll.okafor.${okafor}` })
  } else if (okafor === 'held') {
    out.push({ who: 'okafor', state: 'held', speaker: 'kell', nodeId: 'finale.roll.okafor.held' })
  } else {
    out.push({ who: 'okafor', state: 'absent', speaker: 'kell', nodeId: 'finale.roll.okafor.absent' })
  }

  const trust = kellTrust(career, cfg)
  out.push({ who: 'kell', state: trust, speaker: 'kell', nodeId: `finale.roll.kell.${trust}` })

  // Duarte: only if she was met (Mission 4 offered) and Hale doesn't know about her.
  const metDuarte = story.missions.some((m) => m.id === 'm4')
  if (metDuarte && !hasFlag(career, 'hale_knows') && !hasFlag(career, 'duarte_exposed')) {
    out.push({ who: 'duarte', state: 'present', speaker: 'duarte', nodeId: 'finale.roll.duarte' })
  }

  for (const a of ASSISTED) {
    if (story.flags[a.flag] === true) {
      out.push({ who: 'claim', state: String(a.tier), speaker: 'kell', nodeId: `finale.roll.claim.${a.tier}` })
    }
  }
  return out
}

/** Every node id the roll call can name (the data test checks `comms/finale.json` has each). */
export function rollCallNodeIds(): string[] {
  return [
    'finale.roll.reyes.absent',
    'finale.roll.reyes.cheques',
    'finale.roll.reyes.ally',
    'finale.roll.reyes.cool',
    'finale.roll.reyes.hostile',
    'finale.roll.okafor.aboard',
    'finale.roll.okafor.safe',
    'finale.roll.okafor.held',
    'finale.roll.okafor.absent',
    'finale.roll.kell.high',
    'finale.roll.kell.wary',
    'finale.roll.kell.low',
    'finale.roll.duarte',
    ...ASSISTED.map((a) => `finale.roll.claim.${a.tier}`),
  ]
}
